'use client';

import { Form, Typography } from '@polyseme/design-system';
import { formatPercent } from '@polyseme/utils/number/number-utils';
import { useTranslations } from 'next-intl';
import { Suspense, type RefObject } from 'react';

import { Helper } from '~/landing/components';
import {
  getRateByRiskProfile,
  useFutureSavingsForm,
} from '~/landing/core/simulator/future-savings-planner';
import { TaxSavingsSimulator } from '~/landing/core/simulator/tax-savings-simulator';

import {
  ContactCard,
  FloatingCta,
  FutureSavingsPlanner,
  InvestmentDelayImpact,
} from './components';

type ClientProps = {
  contactCardRef?: RefObject<HTMLDivElement | null>;
};

export const Client = ({ contactCardRef }: ClientProps) => {
  const t = useTranslations('landing');

  const form = useFutureSavingsForm();

  const riskProfile = form.watch('futureSavingsPlanner.riskProfile');

  return (
    <>
      <Form {...form}>
        <form>
          <section className="mx-auto px-4 py-8 lg:max-w-screen-2xl lg:px-8">
            <Typography as="h2" className="mb-6" variant="title-1">
              {t('retraite.futureSavingsPlanner.title')}
            </Typography>

            <FutureSavingsPlanner />

            <Helper className="mt-4">
              {t('retraite.futureSavingsPlanner.information', {
                riskProfile: t(`retraite.riskProfile.${riskProfile}.name`),
                medianPercentRate: formatPercent(
                  getRateByRiskProfile(riskProfile).median
                ),
              })}
            </Helper>
          </section>
          <section className="bg-brand-background w-full px-4 py-16">
            <InvestmentDelayImpact />
          </section>
          <section className="mx-auto px-4 py-8 lg:max-w-screen-2xl lg:px-8">
            <Typography as="h3" className="mb-6" variant="title-2">
              {t('retraite.taxSavingsSimulator.title')}
            </Typography>

            <TaxSavingsSimulator />

            <Helper className="mt-4">
              {t('retraite.taxSavingsSimulator.information')}
            </Helper>
          </section>
        </form>
      </Form>

      <FloatingCta>
        <section
          ref={contactCardRef}
          className="bg-brand-background w-full px-4 py-16"
        >
          <Suspense>
            <ContactCard simulation={form.getValues()} />
          </Suspense>
        </section>
      </FloatingCta>
    </>
  );
};
